import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';

import { AssignmentScoring } from './assignment-scoring.component';
import { IRecordCompositeScoreInput, IRecordUserActionsResults } from '../../Score/ManageEvaluations/manage-evaluations.interface';
import { getAuthUser } from '../../../lib/services/auth.service';
import { useQueueOpen } from '../../../lib/stores/connectivity.store';
import {
  bufferScoreAction,
  forEachBufferedAction,
  transformCompScoreOperationToAction,
} from '../../../lib/utils/user-session.service';
import { useBufferedScoreSubmit } from '../../../lib/services/score-submit.service';
import { checkVideoFilesExist, setVideoAvailability, unavailableFilenamesFor } from '../../../lib/utils/video.utils';
import { assignmentScoreFromAction, clipFilenames, firstUnscoredGroupIndex, groupItemsByClip, makeVideoSkillKey } from './assignment-scoring.utils';
import { useLatestRef } from '../../../lib/hooks/use-latest-ref';
import { usePendingScoreBuffer } from '../../../lib/hooks/use-pending-score-buffer';
import fpSet from 'lodash/fp/set';
import fpUnset from 'lodash/fp/unset';
import { restoreFailedScoreRemoval, revertFailedScoreWrite } from '../../../lib/helpers/score-buffer.helpers';

interface IAssignmentScoringContainerProps {
  assignment?: ICalibrationAssignment;
  loading: boolean;
  fetchError: boolean;
  submitScores: () => Promise<IRecordUserActionsResults | undefined>;
  hasUnsavedScores: boolean;
  setHasUnsavedScores: (value: boolean) => void;
  isSaving: boolean;
}

export const AssignmentScoringContainer: React.FC<IAssignmentScoringContainerProps> = ({
  assignment,
  loading,
  fetchError,
  submitScores,
  hasUnsavedScores,
  setHasUnsavedScores,
  isSaving,
}) => {
  const user = getAuthUser();
  const isQueueOpen = useQueueOpen();
  const assignmentId = assignment && assignment._id;
  const [compositeScoreHash, setCompositeScoreHash] = useState<{ [videoSkillKey: string]: IRecordCompositeScoreInput }>({});
  const { pendingScoreKeys, trackScoreWrite, trackScoreRemoval, resetPendingScores } = usePendingScoreBuffer();
  // bufferLoaded gates the scoring view's mount until buildHash runs, so initialIndex
  // is computed from the populated hash rather than the empty initial one.
  const [initialIndex, setInitialIndex] = useState(0);
  const [bufferLoaded, setBufferLoaded] = useState(false);

  const filenames = useMemo(() => clipFilenames(assignment), [assignment]);
  // Clips confirmed unreachable; excluded from scoring. Seeded from the shared cache (instant on
  // a revisit), then corrected by a background probe — the scorer never blocks on that probe.
  const [unavailableFilenames, setUnavailableFilenames] = useState<Set<string>>(() => unavailableFilenamesFor(filenames));
  const unavailableRef = useLatestRef(unavailableFilenames);

  const isMyAction = useCallback(
    (action: IUserAction) => !!assignmentScoreFromAction(action, assignmentId),
    [assignmentId],
  );

  // Stable proxy for the videos and their scored-state: changes only when an item's id
  // or compositeScoreId changes, so the replay effect skips redundant whole-buffer reads.
  const itemsKey = useMemo(
    () => (assignment?.items || []).map((item) => `${item.calibrationVideoId}:${item.compositeScoreId || ''}`).join('|'),
    [assignment?.items],
  );

  const { hasSaveError } = useBufferedScoreSubmit({
    user, isSaving, isQueueOpen, hasUnsavedScores, setHasUnsavedScores, submitScores, isMyAction,
    canSubmit: !!assignment,
  });

  // A different assignment must re-resolve its own buffered scores before we resume; drop the
  // buffer gate and re-seed availability from the cache so nothing carries over.
  useEffect(() => {
    setBufferLoaded(false);
    resetPendingScores();
    setUnavailableFilenames(unavailableFilenamesFor(filenames));
  }, [assignmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filenames-only proxy: re-probe existence when the clip files change, not per score sync.
  const filenamesKey = useMemo(() => filenames.join('|'), [filenames]);

  // Warm clip availability in the background and drop any unreachable clips — never blocks the
  // scorer (render proceeds on the cache-seeded set while this resolves). See CLAUDE.md.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    checkVideoFilesExist(filenames).then((reachable) => {
      if (!cancelled) setUnavailableFilenames(new Set(filenames.filter((filename) => !reachable.has(filename))));
    });
    return () => { cancelled = true; };
    // filenamesKey proxies the filenames this effect reads off `assignment`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, assignmentId, filenamesKey]);

  // Replay buffered (unsynced) scores for THIS assignment so a score survives a
  // reload until it syncs. Scoped on calibrationAssignmentId so others don't leak.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;

    const buildHash = async () => {
      const nextHash: { [videoSkillKey: string]: IRecordCompositeScoreInput } = {};

      if (user && assignmentId) {
        try {
          await forEachBufferedAction(user, (action) => {
            const score = assignmentScoreFromAction(action, assignmentId);
            if (!score) return;
            const key = makeVideoSkillKey(score);
            if (action.actionType === 'compositeScore') nextHash[key] = score;
            else delete nextHash[key];
          });
        } catch (error) {
          // The scorer still opens, without this device's unsynced scores painted, rather than spinning forever.
          console.error('AssignmentScoringContainer: could not read buffered scores; opening without them', error);
        }
      }

      if (cancelled) return;
      setCompositeScoreHash(nextHash);
      setInitialIndex(firstUnscoredGroupIndex(groupItemsByClip(assignment, unavailableRef.current), nextHash));
      setBufferLoaded(true);
    };

    buildHash();
    return () => { cancelled = true; };
    // itemsKey captures every part of `assignment` read here; unavailableRef is read, not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, assignmentId, user, itemsKey]);

  const handleRecordCompositeScore = useCallback((score: IRecordCompositeScoreInput) => {
    const key = makeVideoSkillKey(score);
    const { loggedAt, clientId, buffered } = bufferScoreAction(user, score, transformCompScoreOperationToAction);
    trackScoreWrite(key, buffered, () => {
      setCompositeScoreHash(prev => revertFailedScoreWrite(prev, [key], loggedAt));
    });
    setCompositeScoreHash(prev => fpSet([key], { ...score, loggedAt, clientId }, prev));
    setHasUnsavedScores(true);
  }, [user, setHasUnsavedScores, trackScoreWrite]);

  const handleRemoveCompositeScore = useCallback((score: IRecordCompositeScoreInput) => {
    const key = makeVideoSkillKey(score);
    // The item builds the op from the latest hash, so it carries the value being removed.
    const previous = score.raw !== '' ? score : undefined;
    const { buffered } = bufferScoreAction(user, score, transformCompScoreOperationToAction, { remove: true });
    trackScoreRemoval(key, buffered, () => {
      setCompositeScoreHash(prev => restoreFailedScoreRemoval(prev, [key], previous));
    });
    setCompositeScoreHash(prev => fpUnset([key], prev));
    setHasUnsavedScores(true);
  }, [user, setHasUnsavedScores, trackScoreRemoval]);

  // A clip that passed the HEAD probe (405/501/5xx/network are kept as indeterminate) can still fail
  // at actual playback. Record the verdict in the shared cache and exclude the clip so it's never scored.
  const handleVideoUnavailable = useCallback((filename: string) => {
    setVideoAvailability(filename, false);
    setUnavailableFilenames((prev) => (prev.has(filename) ? prev : new Set(prev).add(filename)));
  }, []);

  if (!bufferLoaded) return <CircularProgress />;

  return (
    <AssignmentScoring
      key={assignmentId}
      assignment={assignment}
      loading={loading}
      fetchError={fetchError}
      initialIndex={initialIndex}
      unavailableFilenames={unavailableFilenames}
      onVideoUnavailable={handleVideoUnavailable}
      compositeScoreHash={compositeScoreHash}
      pendingScoreKeys={pendingScoreKeys}
      recordCompositeScore={handleRecordCompositeScore}
      removeCompositeScore={handleRemoveCompositeScore}
      hasUnsavedScores={hasUnsavedScores}
      hasSaveError={hasSaveError}
      isSaving={isSaving}
    />
  );
};
